import { useState, type ReactNode } from 'react';
import {
  ApiError,
  createFailureReasonCode,
  createTaskType,
  disableFailureReasonCode,
  disableTaskType,
  fetchFailureReasonCodes,
  fetchTaskTypes,
} from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import {
  KATEGORI_ALASAN,
  KATEGORI_TUGAS,
  normalkanKode,
  periksaAlasanGagal,
  periksaJenisTugas,
  type NilaiKategoriTugas,
} from '../lib/setelan-tugas';
import { ErrorCard, LoadingState } from '../states';

/**
 * Setelan tugas — dua daftar yang harus ada sebelum organisasi dapat bekerja.
 *
 * Tanpa jenis tugas, tidak ada tugas yang dapat dibuat. Tanpa kode alasan gagal, pengiriman yang
 * gagal tidak dapat ditandai. Sebelum layar ini, keduanya hanya dapat dibuat lewat skrip
 * provisioning — insinyur berkredensial produksi, untuk setiap pelanggan.
 *
 * SIFAT BUKTI TIDAK DAPAT DIPILIH DI SINI, dan itu disengaja. Yang dipilih pemakai adalah
 * KATEGORI; server yang menurunkan apakah jenis itu menuntut foto dan tanda tangan. Ketetapan
 * Owner 3 September 2026, setelah menolak versi yang hanya mematok di layar — sakelar bebas
 * membuat "Pengiriman tanpa bukti" mungkin, dan seluruh nilai jual bukti yang tidak dapat dikarang
 * hilang tanpa satu pun galat.
 */
export function SetelanTugasPage(): ReactNode {
  const session = useSession();
  const jenis = useAsync(() => fetchTaskTypes(session.auth), [session.auth]);
  const alasan = useAsync(() => fetchFailureReasonCodes(session.auth), [session.auth]);

  return (
    <>
      <section className="card">
        <h2>Setelan tugas</h2>
        <p className="muted small">
          Jenis tugas menentukan pekerjaan apa yang dapat ditugaskan ke kurir. Kode alasan gagal
          dipakai kurir saat pengiriman tidak dapat diselesaikan.
        </p>
      </section>

      <BagianJenisTugas state={jenis} />
      <BagianAlasanGagal state={alasan} />
    </>
  );
}

function BagianJenisTugas({
  state,
}: {
  readonly state: ReturnType<typeof useAsync<readonly import('../api/types').TaskTypeResponse[]>>;
}): ReactNode {
  const session = useSession();
  const [kode, setKode] = useState('');
  const [nama, setNama] = useState('');
  const [kategori, setKategori] = useState<NilaiKategoriTugas>('PENGIRIMAN');
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<ApiError | null>(null);

  const daftar = state.state.status === 'ready' ? state.state.data : [];
  const masalah = periksaJenisTugas({
    kode,
    nama,
    kategori,
    kodeTerpakai: daftar.map((t) => t.code),
  });
  const terpilih = KATEGORI_TUGAS.find((k) => k.nilai === kategori);

  async function jalankan(aksi: () => Promise<unknown>): Promise<void> {
    setSibuk(true);
    setGalat(null);
    try {
      await aksi();
      state.reload();
    } catch (e: unknown) {
      setGalat(e instanceof ApiError ? e : new ApiError(0, 'UNKNOWN', 'Gagal.', null));
    } finally {
      setSibuk(false);
    }
  }

  return (
    <section className="card">
      <h3>Jenis tugas</h3>
      {state.state.status === 'loading' ? <LoadingState label="Memuat…" /> : null}
      {state.state.status === 'error' ? (
        <ErrorCard error={state.state.error} onRetry={state.reload} />
      ) : null}
      {galat ? <ErrorCard error={galat} /> : null}

      <ul className="list ringkas">
        {daftar.map((t) => (
          <li key={t.id} className="list-item">
            <div className="row space-between">
              <span>
                <strong>{t.name}</strong> <span className="muted small">{t.code}</span>
              </span>
              <span className="row">
                {t.requiresProof ? <span className="chip active">wajib bukti</span> : null}
                {!t.isActive ? <span className="muted small">nonaktif</span> : null}
                {t.isActive ? (
                  <button
                    type="button"
                    className="tautan-kecil"
                    disabled={sibuk}
                    onClick={() => void jalankan(() => disableTaskType(session.auth, t.id))}
                  >
                    Nonaktifkan
                  </button>
                ) : null}
              </span>
            </div>
          </li>
        ))}
      </ul>

      <label htmlFor="jt-nama">Nama jenis tugas</label>
      <input
        id="jt-nama"
        value={nama}
        placeholder="mis. Pengiriman reguler"
        onChange={(e) => {
          setNama(e.target.value);
          // Kode mengikuti nama sampai pemakai menyuntingnya sendiri — satu isian, bukan dua.
          if (kode === '' || kode === normalkanKode(nama)) setKode(normalkanKode(e.target.value));
        }}
      />

      <label htmlFor="jt-kode">Kode</label>
      <input id="jt-kode" value={kode} onChange={(e) => setKode(normalkanKode(e.target.value))} />

      <label htmlFor="jt-kat">Kategori</label>
      <select
        id="jt-kat"
        value={kategori}
        onChange={(e) => setKategori(e.target.value as NilaiKategoriTugas)}
      >
        {KATEGORI_TUGAS.map((k) => (
          <option key={k.nilai} value={k.nilai}>
            {k.label}
          </option>
        ))}
      </select>
      {/*
        Akibat kategorinya dinyatakan SEBELUM disimpan, bukan ditemukan sesudahnya. Yang memilih
        adalah pemilik usaha, dan "wajib bukti" menentukan apakah kurirnya dapat menyelesaikan
        tugas tanpa foto.
      */}
      {terpilih ? <p className="muted small">{terpilih.keterangan}</p> : null}

      {masalah.length > 0 && (kode !== '' || nama !== '') ? (
        <ul className="list ringkas" role="alert">
          {masalah.map((m) => (
            <li key={m.bidang} className="warn-text">
              {m.pesan}
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        className="button-primary"
        disabled={sibuk || masalah.length > 0}
        onClick={() =>
          void jalankan(async () => {
            await createTaskType(session.auth, {
              code: normalkanKode(kode),
              name: nama.trim(),
              category: kategori,
            });
            setKode('');
            setNama('');
          })
        }
      >
        Tambah jenis tugas
      </button>
    </section>
  );
}

function BagianAlasanGagal({
  state,
}: {
  readonly state: ReturnType<typeof useAsync<readonly import('../api/types').FailureReasonCode[]>>;
}): ReactNode {
  const session = useSession();
  const [kode, setKode] = useState('');
  const [label, setLabel] = useState('');
  const [kategori, setKategori] = useState<string>('recipient');
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<ApiError | null>(null);

  const daftar = state.state.status === 'ready' ? state.state.data : [];
  const masalah = periksaAlasanGagal({
    kode,
    label,
    kategori,
    kodeTerpakai: daftar.map((a) => a.code),
  });

  async function jalankan(aksi: () => Promise<unknown>): Promise<void> {
    setSibuk(true);
    setGalat(null);
    try {
      await aksi();
      state.reload();
    } catch (e: unknown) {
      setGalat(e instanceof ApiError ? e : new ApiError(0, 'UNKNOWN', 'Gagal.', null));
    } finally {
      setSibuk(false);
    }
  }

  return (
    <section className="card">
      <h3>Alasan pengiriman gagal</h3>
      {galat ? <ErrorCard error={galat} /> : null}

      <ul className="list ringkas">
        {daftar.map((a) => (
          <li key={a.id} className="list-item">
            <div className="row space-between">
              <span>
                <strong>{a.label}</strong> <span className="muted small">{a.code}</span>
              </span>
              <span className="row">
                {!a.isActive ? <span className="muted small">nonaktif</span> : null}
                {a.isActive ? (
                  <button
                    type="button"
                    className="tautan-kecil"
                    disabled={sibuk}
                    onClick={() =>
                      void jalankan(() => disableFailureReasonCode(session.auth, a.id))
                    }
                  >
                    Nonaktifkan
                  </button>
                ) : null}
              </span>
            </div>
          </li>
        ))}
      </ul>

      <label htmlFor="ag-label">Keterangan</label>
      <input
        id="ag-label"
        value={label}
        placeholder="mis. Gudang penerima tutup"
        onChange={(e) => {
          setLabel(e.target.value);
          if (kode === '' || kode === normalkanKode(label)) setKode(normalkanKode(e.target.value));
        }}
      />

      <label htmlFor="ag-kode">Kode</label>
      <input id="ag-kode" value={kode} onChange={(e) => setKode(normalkanKode(e.target.value))} />

      <label htmlFor="ag-kat">Kategori</label>
      <select id="ag-kat" value={kategori} onChange={(e) => setKategori(e.target.value)}>
        {KATEGORI_ALASAN.map((k) => (
          <option key={k.nilai} value={k.nilai}>
            {k.label}
          </option>
        ))}
      </select>

      {masalah.length > 0 && (kode !== '' || label !== '') ? (
        <ul className="list ringkas" role="alert">
          {masalah.map((m) => (
            <li key={m.bidang} className="warn-text">
              {m.pesan}
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        className="button-primary"
        disabled={sibuk || masalah.length > 0}
        onClick={() =>
          void jalankan(async () => {
            await createFailureReasonCode(session.auth, {
              code: normalkanKode(kode),
              label: label.trim(),
              category: kategori,
            });
            setKode('');
            setLabel('');
          })
        }
      >
        Tambah alasan
      </button>
    </section>
  );
}
